import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Partnerdetail.css";
import "./styles/Cisco.css";
import ciscoLogo from "../../assets/images/partners/Cisco.png";

function useCounter(target, duration = 2000, inView) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, inView]);
  return count;
}
function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const portfolio = [
  { area: "Campus Switching", title: "Cisco Catalyst", wide: true, img: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=900&q=80", desc: "The world's most trusted campus switching platform with AI-driven analytics, automation, and intent-based networking. Catalyst 9000 Series delivers hardware-accelerated encryption, UADP ASICs, and embedded assurance for networks of any scale.", chips: ["Catalyst 9200", "Catalyst 9300", "Catalyst 9400", "Catalyst Center", "DNA Assurance"] },
  { area: "Cloud Networking", title: "Cisco Meraki", img: "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?auto=format&fit=crop&w=700&q=80", desc: "Cloud-managed networking with zero-touch provisioning. The Meraki dashboard unifies wireless, switching, security, SD-WAN, and camera management across thousands of sites from a single pane of glass.", chips: ["MR Wi-Fi 6E", "MS Switches", "MX Security"] },
  { area: "SD-WAN & SASE", title: "Cisco SD-WAN / Viptela", tall: true, img: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=700&q=80", desc: "Transform branch connectivity with software-defined WAN. Cisco SD-WAN optimizes application performance across MPLS, broadband, and LTE links — with centralized policy, security, and analytics.", chips: ["vEdge", "vManage", "FortiSASE Gateway", "SASE Cloud"] },
  { area: "Collaboration", title: "Cisco Webex", img: "https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&w=700&q=80", desc: "AI-powered meetings, calling, messaging, and whiteboarding. Webex integrates seamlessly with Cisco room devices — delivering broadcast-quality audio and AI noise cancellation for hybrid teams.", chips: ["Webex Meetings", "Webex Calling", "Room Bars", "Board Pro"] },
  { area: "Network Analytics", title: "ThousandEyes", wide: true, img: "https://images.unsplash.com/photo-1573164713988-8665fc963095?auto=format&fit=crop&w=900&q=80", desc: "End-to-end visibility across your network, cloud, and the internet. ThousandEyes surfaces performance degradation across every hop — from the campus LAN through SaaS applications — before users notice anything.", chips: ["Internet Insights", "Cloud & App Tests", "BGP Routing", "AI Correlated Events"] },
];

const secureFeats = [
  {
    tag: "🛡️ Network Security",
    title: "Cisco Secure Firewall & NGFW",
    body: "Cisco Secure Firewall (formerly Firepower) delivers industry-leading threat protection with deep packet inspection, encrypted traffic analytics, and integrated IPS. Powered by Talos threat intelligence — the world's largest commercial security research team — Cisco NGFW blocks threats before they penetrate your perimeter.",
    img: "https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=900&q=80",
    points: ["Firepower 1000 through 4200 series hardware appliances", "Cisco FTD software for virtual and cloud deployments", "Encrypted Traffic Analytics without decryption overhead", "Cisco Talos threat intelligence — 1.7M security researchers", "Integrated Snort 3 IPS with AI-powered signature updates"],
    rev: false,
  },
  {
    tag: "🔑 Identity & Access",
    title: "Cisco Duo & Zero Trust Access",
    body: "Duo Multi-Factor Authentication protects every application access with device trust verification, push notifications, and continuous posture assessment. As part of Cisco's Zero Trust architecture, Duo ensures only verified users on healthy devices can access corporate resources — on-premises or in the cloud.",
    img: "https://images.unsplash.com/photo-1614064641938-3bbee52942c7?auto=format&fit=crop&w=900&q=80",
    points: ["MFA for every application — cloud, on-prem, legacy", "Device Health Assessment at login and throughout sessions", "ZTNA application-level access control replacing VPN", "Passwordless authentication with FIDO2 and passkeys", "Adaptive access policies based on risk scoring"],
    rev: true,
  },
  {
    tag: "☁️ Cloud & DNS Security",
    title: "Cisco Umbrella — Secure Internet Gateway",
    body: "Cisco Umbrella stops threats at the DNS layer before connections are established — blocking malware, phishing, and ransomware without impacting performance. As a cloud-native SASE component, Umbrella protects every user and device regardless of location, eliminating the gaps left by VPN-centric security.",
    img: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=900&q=80",
    points: ["DNS-layer security blocking threats before connection", "Secure Web Gateway with full URL and content inspection", "CASB visibility and control for shadow IT and SaaS", "Remote Browser Isolation for risky web categories", "Cloud-native SASE integration with Cisco SD-WAN"],
    rev: false,
  },
];

const industries = [
  { icon: "🏥", name: "Healthcare", desc: "Secure medical IoT, clinical Wi-Fi with SLA guarantees, and HIPAA-compliant network segmentation for patient data protection." },
  { icon: "🏦", name: "Financial Services", desc: "High-availability trading networks, PCI-DSS compliant infrastructure, and zero-trust segmentation for financial application environments." },
  { icon: "🎓", name: "Education", desc: "Campus Wi-Fi 6E for high-density lecture halls, SD-WAN for multi-site institutions, and Webex collaboration for hybrid learning." },
  { icon: "🏛️", name: "Government", desc: "FIPS-compliant Cisco equipment, FedRAMP authorized cloud services, and ISE-based policy enforcement for federal environments." },
  { icon: "🏭", name: "Manufacturing", desc: "Industrial switching with OT/IT convergence, Cisco IND for factory floor visibility, and ruggedized wireless for shop floors." },
  { icon: "🛒", name: "Retail", desc: "Meraki cloud networking for distributed stores, PCI-compliant segmentation, and in-store analytics from MV camera platforms." },
  { icon: "🏗️", name: "Construction & Real Estate", desc: "Meraki rapid deployment for construction sites, guest Wi-Fi for commercial properties, and SD-WAN for geographically distributed operations." },
  { icon: "🚚", name: "Logistics & Distribution", desc: "Warehouse Wi-Fi for scan guns and AGVs, SD-WAN connecting distribution centers, and ThousandEyes monitoring for critical applications." },
];

const aiFeats = [
  { icon: "🤖", title: "Cisco AI Network Analytics", desc: "Machine learning baselines normal behavior and surfaces anomalies across campus, branch, and WAN automatically." },
  { icon: "🔮", title: "Predictive Troubleshooting", desc: "Catalyst Center identifies degrading access points, switches, and links before users experience issues." },
  { icon: "📡", title: "Automated Assurance", desc: "End-to-end network assurance from client to cloud — with root cause analysis in minutes, not hours." },
  { icon: "⚡", title: "AI-Optimized Radio Management", desc: "Real-time RF optimization across Catalyst Center adjusts channels, power, and bands for peak performance." },
];

const certifications = [
  { icon: "🏆", name: "Cisco Gold Partner", desc: "Gold-tier Cisco partner — top 5% globally by certification and specialization depth" },
  { icon: "🎓", name: "CCNP Enterprise", desc: "Enterprise network design, implementation, and operations certification" },
  { icon: "🔒", name: "CCNP Security", desc: "Enterprise security architecture, firewall, and zero-trust implementation" },
  { icon: "☁️", name: "Cisco Meraki CMSS", desc: "Certified Meraki Solutions Specialist — cloud networking deployment expertise" },
];

const benefits = [
  { icon: "🔗", title: "Single-Platform Simplicity", desc: "Replace multi-vendor complexity with a unified Cisco architecture — one policy engine, one management dashboard, one security fabric across networking and security." },
  { icon: "🛡️", title: "Built-In Security at Every Layer", desc: "Cisco integrates security into the network fabric itself — switches, APs, and routers enforce policy, detect anomalies, and quarantine threats without additional appliances." },
  { icon: "📊", title: "AI-Driven Operational Efficiency", desc: "Cisco AI Network Analytics reduces mean time to resolution from hours to minutes — automatically identifying root causes that manual troubleshooting would take days to find." },
  { icon: "🌐", title: "Seamless Hybrid Work Experience", desc: "Webex collaboration and Cisco SD-WAN deliver consistent application performance and meeting quality for remote, office, and hybrid workers equally." },
];

const faqData = [
  { q: "What is Cisco Catalyst Center and how does it differ from traditional network management?", a: "Cisco Catalyst Center (formerly DNA Center) is an intent-based network management and automation platform. Unlike traditional SNMP-based management tools that simply monitor devices, Catalyst Center allows administrators to express intent — defining what the network should do — and the system translates those policies into device configurations automatically. It includes AI-powered assurance, automated troubleshooting, and end-to-end network analytics across your entire campus and branch infrastructure. ALG Data Guard deploys and operates Catalyst Center for enterprise clients." },
  { q: "How does Cisco Meraki differ from Cisco's on-premise networking products?", a: "Cisco Meraki is a cloud-managed networking platform designed for simplicity and distributed management. Unlike Cisco's on-premise platforms (Catalyst, Firepower) which require local controllers and management infrastructure, Meraki devices are 100% managed through the cloud dashboard — with zero-touch provisioning, automatic firmware updates, and real-time visibility from anywhere. Meraki is ideal for organizations with distributed branch locations, limited IT staff, or multi-site retail environments. ALG Data Guard helps clients choose between Meraki and traditional Cisco based on their scale, IT model, and operational requirements." },
  { q: "What is Cisco's Zero Trust architecture and how does ALG Data Guard implement it?", a: "Cisco's Zero Trust architecture comprises three pillars: Workforce (Duo MFA and device trust), Workloads (application access control through ZTNA), and Workplace (network segmentation and micro-segmentation using ISE). ALG Data Guard implements Cisco Zero Trust by deploying Duo across all applications, configuring Cisco ISE for 802.1X network access control and dynamic VLAN assignment, deploying Cisco Umbrella for cloud-delivered security, and establishing application-level ZTNA access that replaces traditional full-tunnel VPN." },
  { q: "What Cisco certifications does ALG Data Guard hold?", a: "ALG Data Guard holds Cisco Gold Partner status — in the top 5% of Cisco partners globally. Our engineering team holds active CCNA, CCNP Enterprise, CCNP Security, Cisco Meraki Solutions Specialist (CMSS), Cisco CX Specialization, and Cisco Collaboration certifications. We maintain ongoing training requirements and hold Cisco Advanced Specializations in Enterprise Networks and Security." },
  { q: "Can ALG Data Guard manage our existing Cisco infrastructure on an ongoing basis?", a: "Yes. ALG Data Guard offers managed Cisco services including: proactive network monitoring and alerting through Cisco ThousandEyes and Catalyst Center, firmware and patch management, configuration backup and change management, incident response, capacity planning, and quarterly business reviews. We offer co-managed models (augmenting your team) and fully managed models (ALG Data Guard as primary Cisco operator) depending on your internal IT capabilities." },
  { q: "What is the difference between Cisco Secure Firewall and Cisco Meraki MX?", a: "Cisco Secure Firewall (Firepower series) is Cisco's enterprise-grade next-generation firewall designed for organizations with complex security requirements — including encrypted traffic analytics, advanced malware protection, Talos IPS, application visibility and control, and detailed security policy with zone-based segmentation. Cisco Meraki MX is a cloud-managed security appliance designed for simplicity — excellent for branch offices, distributed retail, and organizations using Meraki's full stack. For most large enterprise and compliance-driven environments, ALG Data Guard recommends Cisco Secure Firewall. For distributed branch networks managed by centralized IT, Meraki MX delivers significant operational simplicity." },
];

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
);
const ChevronDown = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
);

export default function Cisco() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [metricsRef, metricsInView] = useInView(0.3);
  const c1 = useCounter(800, 2200, metricsInView);
  const c2 = useCounter(99, 2000, metricsInView);
  const c3 = useCounter(25, 2000, metricsInView);
  const c4 = useCounter(180, 2000, metricsInView);

  return (
    <div className="cs-page pd-page">

      {/* ── Identity Band ── */}
      <div className="pd-identity-band">
        <div className="pd-identity-inner">
          <img src={ciscoLogo} alt="Cisco" className="pd-partner-logo" />
          <div className="pd-identity-divider" aria-hidden="true" />
          <div className="pd-identity-text">
            <div className="pd-eyebrow pd-eyebrow--dark">
              <span className="pd-live-dot pd-live-dot--dark" />
              Enterprise Networking & Security Partner
            </div>
            <h1 className="pd-identity-name">ALG Data Guard × Cisco</h1>
            <p className="pd-identity-desc">
              Cisco is the worldwide leader in enterprise networking, connecting 95% of Fortune 500 companies
              through networking, security, collaboration, and observability solutions. ALG Data Guard, a Cisco
              Gold Certified Partner, designs, deploys, and manages the full Cisco portfolio — from campus
              switching and cloud-managed Wi-Fi to zero-trust security and AI-powered networking.
            </p>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="cs-hero">
        <div className="cs-hero__topbar" aria-hidden="true" />
        <div className="cs-hero__grid-bg" aria-hidden="true" />
        <div className="cs-hero__glow" aria-hidden="true" />
        <div className="cs-hero__inner">
          <div>
            <div className="cs-hero__kicker">
              <span className="cs-hero__kicker-dot" />
              Cisco Gold Certified Partner
            </div>
            <h2 className="cs-hero__title">
              The Enterprise Network<br /><span>Starts Here.</span>
            </h2>
            <p className="cs-hero__body">
              From the campus access layer to cloud-delivered security and AI-powered analytics,
              Cisco's integrated architecture gives enterprises a single platform for networking,
              security, and collaboration. ALG Data Guard's Cisco-certified engineers design it,
              deploy it, and keep it running.
            </p>
            <div className="cs-hero__actions">
              <button className="cs-btn cs-btn--primary" onClick={() => navigate("/contact")}>
                Design My Cisco Architecture <ArrowIcon />
              </button>
              <button className="cs-btn cs-btn--ghost" onClick={() => navigate("/contact")}>
                Talk to a Cisco Expert
              </button>
            </div>
            <div className="cs-hero__proof">
              <div>
                <div className="cs-hero__proof-val">#1</div>
                <div className="cs-hero__proof-label">Enterprise Networking Globally</div>
              </div>
              <div>
                <div className="cs-hero__proof-val">95%</div>
                <div className="cs-hero__proof-label">Fortune 500 Run Cisco</div>
              </div>
              <div>
                <div className="cs-hero__proof-val">Gold</div>
                <div className="cs-hero__proof-label">ALG Data Guard Partner Tier</div>
              </div>
            </div>
          </div>

          {/* Network Topology Diagram */}
          <div className="cs-topology-card">
            <div className="cs-topology-title">Cisco Reference Architecture — Enterprise Campus</div>
            <div className="cs-topo-row">
              <div className="cs-topo-node cs-topo-node--core">🏢 Core Layer — Catalyst 9500</div>
            </div>
            <div className="cs-arch-connector" style={{ textAlign:"center", color:"rgba(4,159,217,0.5)", margin:"0.2rem 0" }}>⬇</div>
            <div className="cs-topo-row" style={{ gap:"0.5rem" }}>
              <div className="cs-topo-node cs-topo-node--dist" style={{ flex:"1" }}>🔀 Distribution — Catalyst 9400</div>
              <div className="cs-topo-node cs-topo-node--dist" style={{ flex:"1" }}>🔀 Distribution — Catalyst 9400</div>
            </div>
            <div className="cs-arch-connector" style={{ textAlign:"center", color:"rgba(4,159,217,0.5)", margin:"0.2rem 0" }}>⬇</div>
            <div className="cs-topo-row" style={{ gap:"0.5rem" }}>
              <div className="cs-topo-node cs-topo-node--dist" style={{ flex:"1", fontSize:"0.78rem" }}>📡 Access — Catalyst 9300</div>
              <div className="cs-topo-node cs-topo-node--dist" style={{ flex:"1", fontSize:"0.78rem" }}>📶 Wi-Fi 6E — Catalyst AP</div>
            </div>
            <div className="cs-arch-connector" style={{ textAlign:"center", color:"rgba(4,159,217,0.5)", margin:"0.2rem 0" }}>⬇</div>
            <div className="cs-topo-node cs-topo-node--sec">🔒 Cisco Secure Firewall + Duo ZTNA + Umbrella</div>
            <div className="cs-topo-badges" style={{ marginTop:"0.8rem" }}>
              <span className="cs-topo-badge">Catalyst Center</span>
              <span className="cs-topo-badge">ThousandEyes</span>
              <span className="cs-topo-badge">Cisco ISE</span>
              <span className="cs-topo-badge">SecureX</span>
            </div>
            <div className="cs-topo-footer">🛡️ Managed by ALG Data Guard — Cisco Gold Partner</div>
          </div>
        </div>
      </section>

      {/* ── Statement Band ── */}
      <div className="cs-statement">
        <div className="cs-statement__inner">
          <div className="cs-statement__text">
            Cisco powers the networks of 95% of Fortune 500 companies — and ALG Data Guard brings that same enterprise-grade technology to organizations of every size.
          </div>
          <div className="cs-statement__cta">
            <button className="cs-btn cs-btn--ghost" style={{ color:"#000", borderColor:"rgba(0,0,0,0.25)", background:"rgba(0,0,0,0.08)" }} onClick={() => navigate("/contact")}>
              Get a Free Assessment <ArrowIcon />
            </button>
          </div>
        </div>
      </div>

      {/* ── Portfolio Bento ── */}
      <section className="cs-portfolio">
        <div className="cs-shell">
          <div className="cs-portfolio__head">
            <span className="cs-pill"><span className="cs-dot" /> Cisco Portfolio</span>
            <h2 className="cs-section-title">The Complete Cisco Solution Stack</h2>
            <p className="cs-section-sub">
              ALG Data Guard is certified and experienced across every major Cisco platform — designing integrated architectures that span campus, cloud, SD-WAN, security, and collaboration.
            </p>
          </div>
          <div className="cs-bento">
            {portfolio.map((item) => (
              <div key={item.title} className={`cs-bento-card${item.wide ? " cs-bento-card--wide" : ""}${item.tall ? " cs-bento-card--tall" : ""}`}>
                <img src={item.img} alt={item.title} className="cs-bento-card__img" />
                <div className="cs-bento-card__body">
                  <span className="cs-bento-card__area">{item.area}</span>
                  <h3 className="cs-bento-card__title">{item.title}</h3>
                  <p className="cs-bento-card__desc">{item.desc}</p>
                  <div className="cs-bento-card__chips">{item.chips.map(c => <span key={c} className="cs-bento-card__chip">{c}</span>)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Cisco Secure — Zig-Zag ── */}
      <section className="cs-secure">
        <div className="cs-shell">
          <div className="cs-secure__head">
            <span className="cs-pill" style={{ background:"rgba(4,159,217,0.2)", borderColor:"rgba(4,159,217,0.4)", color:"#7dd3ed" }}>
              <span className="cs-dot" /> Cisco Secure
            </span>
            <h2 className="cs-section-title" style={{ color:"#fff" }}>Comprehensive Security, Built Into the Network</h2>
            <p className="cs-section-sub" style={{ color:"rgba(255,255,255,0.75)", margin:"0 auto" }}>
              Cisco's integrated security approach embeds protection into every network layer — delivering threat prevention, zero-trust access, and cloud security without adding complexity.
            </p>
          </div>
          <div className="cs-secure__features">
            {secureFeats.map(feat => (
              <div key={feat.title} className={`cs-secure-feat${feat.rev ? " cs-secure-feat--rev" : ""}`}>
                <img src={feat.img} alt={feat.title} className="cs-secure-feat__img" />
                <div>
                  <span className="cs-secure-feat__tag">{feat.tag}</span>
                  <h3 className="cs-secure-feat__title">{feat.title}</h3>
                  <p className="cs-secure-feat__body">{feat.body}</p>
                  <ul className="cs-secure-feat__points">{feat.points.map(p => <li key={p}>{p}</li>)}</ul>
                  <button className="cs-btn cs-btn--primary" onClick={() => navigate("/contact")}>Learn More <ArrowIcon /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Collaboration ── */}
      <section className="cs-collab">
        <div className="cs-shell">
          <div className="cs-collab__inner">
            <div className="cs-collab__img-wrap">
              <img
                src="https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&w=900&q=80"
                alt="Cisco Webex collaboration"
                className="cs-collab__img"
              />
              <div className="cs-collab__img-badge">
                Webex<small>AI-Powered Suite</small>
              </div>
            </div>
            <div>
              <span className="cs-pill"><span className="cs-dot" /> Cisco Webex</span>
              <h2 className="cs-collab__title">Where Work Happens — Meetings, Calling, Messaging, All in One</h2>
              <p className="cs-collab__body">
                Cisco Webex is the world's most secure and feature-complete collaboration platform — trusted by governments, financial institutions, and Fortune 500 enterprises for their most sensitive communications. AI-powered noise suppression, real-time transcription, and meeting summaries keep teams productive across any device, anywhere.
              </p>
              <p className="cs-collab__body">
                ALG Data Guard deploys and manages the full Webex ecosystem — from cloud calling replacement for legacy PBX systems to Webex Room and Board devices for immersive conference room experiences.
              </p>
              <div className="cs-collab__suite">
                {["Webex Meetings", "Webex Calling", "Webex Messaging", "Webex Events", "Cisco Room Bar", "Cisco Board Pro", "Contact Center", "Webex AI Agents"].map(s => (
                  <div key={s} className="cs-collab__suite-item">📌 {s}</div>
                ))}
              </div>
              <button className="cs-btn cs-btn--primary" onClick={() => navigate("/contact")}>
                Explore Webex Solutions <ArrowIcon />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── AI Networking ── */}
      <section className="cs-ai">
        <div className="cs-shell">
          <div className="cs-ai__inner">
            <div>
              <span className="cs-pill" style={{ background:"rgba(4,159,217,0.2)", borderColor:"rgba(4,159,217,0.35)", color:"#7dd3ed" }}>
                <span className="cs-dot" /> AI-Powered Networking
              </span>
              <h2 className="cs-section-title" style={{ color:"#fff" }}>Networking That Learns, Adapts, and Heals Itself</h2>
              <p className="cs-section-sub" style={{ color:"rgba(255,255,255,0.75)", marginBottom:"0.5rem" }}>
                Cisco Catalyst Center and ThousandEyes combine to deliver an AI-powered network operations platform that identifies, diagnoses, and resolves issues faster than any manual process.
              </p>
              <div className="cs-ai__features">
                {aiFeats.map(f => (
                  <div key={f.title} className="cs-ai-feature">
                    <div className="cs-ai-feature__icon">{f.icon}</div>
                    <div className="cs-ai-feature__title">{f.title}</div>
                    <p className="cs-ai-feature__desc">{f.desc}</p>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="cs-btn cs-btn--primary" onClick={() => navigate("/contact")}>
                  Explore AI Networking <ArrowIcon />
                </button>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1677442135703-1787eea5ce01?auto=format&fit=crop&w=900&q=80"
              alt="AI-powered network analytics"
              className="cs-ai__img"
            />
          </div>
        </div>
      </section>

      {/* ── Industries ── */}
      <section className="cs-industries">
        <div className="cs-shell">
          <div className="cs-industries__head">
            <span className="cs-pill"><span className="cs-dot" /> Industry Solutions</span>
            <h2 className="cs-section-title">Cisco Solutions Across Every Vertical</h2>
            <p className="cs-section-sub" style={{ margin:"0 auto" }}>Cisco's validated industry-specific architectures and compliance-ready platforms serve regulated and mission-critical environments globally.</p>
          </div>
          <div className="cs-industries__grid">
            {industries.map(ind => (
              <div key={ind.name} className="cs-industry-card">
                <span className="cs-industry-card__icon">{ind.icon}</span>
                <h3 className="cs-industry-card__name">{ind.name}</h3>
                <p className="cs-industry-card__desc">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Metrics ── */}
      <section className="cs-metrics" ref={metricsRef}>
        <div className="cs-shell">
          <div className="cs-metrics__inner">
            <div><div className="cs-metric__val">{c1}+</div><div className="cs-metric__label">Cisco Devices Deployed</div></div>
            <div><div className="cs-metric__val">{c2}.9%</div><div className="cs-metric__label">Network Uptime SLA</div></div>
            <div><div className="cs-metric__val">{c3}+</div><div className="cs-metric__label">Years Cisco Partnership</div></div>
            <div><div className="cs-metric__val">{c4}+</div><div className="cs-metric__label">Enterprise Clients Served</div></div>
          </div>
        </div>
      </section>

      {/* ── Benefits ── */}
      <section className="cs-benefits">
        <div className="cs-shell">
          <div className="cs-benefits__inner">
            <img
              src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=900&q=80"
              alt="Enterprise data center networking"
              className="cs-benefits__img"
            />
            <div>
              <span className="cs-pill"><span className="cs-dot" /> Business Value</span>
              <h2 className="cs-section-title">Why Enterprise IT Leaders Choose Cisco</h2>
              <p className="cs-section-sub" style={{ marginBottom:"2rem" }}>The world's most deployed enterprise networking platform delivers measurable business advantages beyond technical capabilities.</p>
              <div className="cs-benefits__list">
                {benefits.map(b => (
                  <div key={b.title} className="cs-benefit-item">
                    <div className="cs-benefit-item__icon">{b.icon}</div>
                    <div>
                      <div className="cs-benefit-item__title">{b.title}</div>
                      <p className="cs-benefit-item__desc">{b.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Certifications ── */}
      <section className="cs-certs">
        <div className="cs-shell">
          <div className="cs-certs__head">
            <span className="cs-pill"><span className="cs-dot" /> Our Cisco Credentials</span>
            <h2 className="cs-section-title">Validated Cisco Partner Expertise</h2>
            <p className="cs-section-sub" style={{ margin:"0 auto" }}>Our team holds industry's most respected Cisco certifications — ensuring every engagement is led by genuinely qualified network engineers.</p>
          </div>
          <div className="cs-certs__grid">
            {certifications.map(c => (
              <div key={c.name} className="cs-cert-card">
                <div className="cs-cert-card__icon">{c.icon}</div>
                <div className="cs-cert-card__name">{c.name}</div>
                <div className="cs-cert-card__desc">{c.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="cs-faq">
        <div className="cs-shell">
          <div className="cs-faq__head">
            <span className="cs-pill" style={{ background:"rgba(4,159,217,0.2)", borderColor:"rgba(4,159,217,0.4)", color:"#7dd3ed" }}>
              <span className="cs-dot" /> FAQs
            </span>
            <h2 className="cs-section-title" style={{ color:"#fff" }}>Cisco Questions, Answered</h2>
            <p className="cs-section-sub" style={{ color:"rgba(255,255,255,0.7)", margin:"0 auto" }}>Everything you need to know about Cisco networking and how ALG Data Guard delivers it.</p>
          </div>
          <div className="cs-faq__list">
            {faqData.map((faq, i) => (
              <div key={i} className={`cs-faq-item${openFaq === i ? " open" : ""}`}>
                <button className="cs-faq-item__trigger" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                  <span className="cs-faq-item__q">{faq.q}</span>
                  <span className="cs-faq-item__arrow"><ChevronDown /></span>
                </button>
                <div className="cs-faq-item__answer"><div className="cs-faq-item__answer-inner">{faq.a}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="cs-cta">
        <div className="cs-cta__inner">
          <span className="cs-pill" style={{ background:"rgba(4,159,217,0.2)", borderColor:"rgba(4,159,217,0.4)", color:"#7dd3ed", margin:"0 auto 1.2rem" }}>
            <span className="cs-dot" /> Get Started
          </span>
          <h2 className="cs-cta__title">Build the Enterprise Network Your Business Deserves</h2>
          <p className="cs-cta__sub">
            Whether you're modernizing campus infrastructure, extending to the cloud, or implementing zero-trust security — ALG Data Guard's Cisco Gold Certified engineers design, deploy, and manage the right solution for your environment.
          </p>
          <div className="cs-cta__actions">
            <button className="cs-btn cs-btn--primary" onClick={() => navigate("/contact")}>
              Request a Cisco Assessment <ArrowIcon />
            </button>
            <button className="cs-btn cs-btn--ghost" onClick={() => navigate("/contact")}>
              Talk to a Cisco Engineer
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}
